import React from 'react';
import { Control, Controller, FieldErrors } from 'react-hook-form';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { StateCombobox, RequiredIndicator, StaticCombobox, CustomDatePicker } from '@/components/shared';
import { sexOptions, genderOptions, raceOptions } from '@/data/commonDataOptions';
import ErrorMessage from '@/components/shared/ErrorMessage/ErrorMessage';

export interface PersonalDataFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<any>;
    errors?: FieldErrors<any>;
    isSubmitting?: boolean;
    layout?: 'compact' | 'full';
}

const PersonalDataFieldset: React.FC<PersonalDataFieldsetProps> = ({
    context,
    control,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {

    // Converter arrays de strings para formato do StaticCombobox
    const convertToStaticOptions = (options: string[]) =>
        options.map(option => ({ value: option, label: option }));

    const raceStaticOptions = convertToStaticOptions(raceOptions);
    const genderStaticOptions = convertToStaticOptions(genderOptions);

    // Verificar se os campos devem ser mostrados no contexto atual
    const shouldShowContactSection = collaboratorSchemaHelpers.shouldShowField('personalEmail', context);
    const shouldShowBasicDataSection = collaboratorSchemaHelpers.shouldShowField('birthDate', context);

    return (
        <div className="space-y-6" data-testid="personal-data-fieldset">
            {/* Seção de Contato */}
            {shouldShowContactSection && (
                <fieldset className="border p-4 rounded-md" data-testid="fieldset-contact">
                    <legend className="text-sm font-medium px-1 text-gray-400">Contato</legend>
                    <div className={`grid gap-4 pt-2 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                        <div>
                            <label htmlFor="personalEmail" className="block text-sm font-medium text-gray-700">
                                E-mail Pessoal
                                {collaboratorSchemaHelpers.isFieldRequired('personalEmail', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="personalEmail"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="email"
                                        id="personalEmail"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        placeholder="exemplo@email.com"
                                        data-testid="personal-email-input"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="personalEmail" />
                        </div>

                        <div>
                            <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                                Telefone Celular
                                {collaboratorSchemaHelpers.isFieldRequired('phone', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="phone"
                                control={control}
                                render={({ field }) => (
                                    <div className="mt-1">
                                        <PhoneInput
                                            value={field.value || ''}
                                            onChange={field.onChange}
                                            defaultCountry="br"
                                            inputClassName="input-primary w-full"
                                            style={{
                                                '--react-international-phone-height': '40px',
                                                '--react-international-phone-border-width': '1px',
                                                '--react-international-phone-border-color': '#d1d5db',
                                                '--react-international-phone-border-radius': '8px'
                                            } as React.CSSProperties}
                                            disabled={isSubmitting}
                                            countrySelectorStyleProps={{
                                                buttonContentWrapperClassName: 'px-2',
                                                buttonClassName: 'h-[40px] border-r border-gray-300 rounded-l-lg'
                                            }}
                                            inputProps={{
                                                id: 'phone',
                                                style: {
                                                    height: '40px',
                                                    border: '1px solid #d1d5db',
                                                    borderLeft: 'none',
                                                    borderRadius: '0 8px 8px 0',
                                                    outline: 'none',
                                                    padding: '0 12px',
                                                    fontSize: '14px'
                                                }
                                            } as any}
                                        />
                                    </div>
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="phone" />
                        </div>
                    </div>
                </fieldset>
            )}

            {/* Seção de Dados Básicos */}
            {shouldShowBasicDataSection && (
                <fieldset className="border p-4 rounded-md" data-testid="fieldset-basic-data">
                    <legend className="text-sm font-medium px-1 text-gray-400">Dados Básicos</legend>
                    <div className={`grid gap-4 pt-2 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                        <div>
                            <label htmlFor="birthDate" className="block text-sm font-medium text-gray-700">
                                Data de Nascimento
                                {collaboratorSchemaHelpers.isFieldRequired('birthDate', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="birthDate"
                                control={control}
                                render={({ field }) => (
                                    <CustomDatePicker
                                        id="birthDate"
                                        value={field.value || ''}
                                        onChange={field.onChange}
                                        disabled={isSubmitting}
                                        data-testid="birth-date-input"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="birthDate" />
                        </div>

                        <div>
                            <label htmlFor="sex" className="block text-sm font-medium text-gray-700">
                                Sexo
                                {collaboratorSchemaHelpers.isFieldRequired('sex', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="sex"
                                control={control}
                                render={({ field }) => (
                                    <select
                                        {...field}
                                        value={field.value || ''}
                                        id="sex"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="sex-select"
                                    >
                                        <option value="">Selecione...</option>
                                        {sexOptions.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="sex" />
                        </div>

                        <div>
                            <label htmlFor="race" className="block text-sm font-medium text-gray-700">
                                Raça / Etnia
                                {collaboratorSchemaHelpers.isFieldRequired('race', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="race"
                                control={control}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={raceStaticOptions}
                                        selectedValue={field.value || ''}
                                        onChange={field.onChange}
                                        disabled={isSubmitting}
                                        placeholder="Selecione uma raça/etnia"
                                        data-testid="race-combobox"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="race" />
                        </div>

                        <div>
                            <label htmlFor="gender" className="block text-sm font-medium text-gray-700">
                                Gênero
                                {collaboratorSchemaHelpers.isFieldRequired('gender', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="gender"
                                control={control}
                                render={({ field }) => (
                                    <StaticCombobox
                                        options={genderStaticOptions}
                                        selectedValue={field.value || ''}
                                        onChange={field.onChange}
                                        disabled={isSubmitting}
                                        placeholder="Selecione um gênero"
                                        data-testid="gender-combobox"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="gender" />
                        </div>
                    </div>

                    <div className={`grid gap-4 pt-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-3'}`}>
                        <div>
                            <label htmlFor="nationality" className="block text-sm font-medium text-gray-700">
                                Nacionalidade
                                {collaboratorSchemaHelpers.isFieldRequired('nationality', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="nationality"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        id="nationality"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="nationality-input"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="nationality" />
                        </div>

                        <div>
                            <label htmlFor="naturality" className="block text-sm font-medium text-gray-700">
                                Naturalidade (Cidade)
                                {collaboratorSchemaHelpers.isFieldRequired('naturality', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="naturality"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        type="text"
                                        id="naturality"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        data-testid="naturality-input"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="naturality" />
                        </div>

                        <div>
                            <label htmlFor="birthState" className="block text-sm font-medium text-gray-700">
                                Estado (UF)
                                {collaboratorSchemaHelpers.isFieldRequired('birthState', context) && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="birthState"
                                control={control}
                                render={({ field }) => (
                                    <StateCombobox
                                        selectedValue={field.value || ''}
                                        onChange={field.onChange}
                                        disabled={isSubmitting}
                                        data-testid="birth-state-combobox"
                                    />
                                )}
                            />
                            <ErrorMessage errors={errors} fieldPath="birthState" />
                        </div>
                    </div>
                </fieldset>
            )}
        </div>
    );
};

export default PersonalDataFieldset;
